const STORAGE_KEY = "playerNhlStatsGraphPrefs:v2";

export const CHART_TYPES = [
    { id: "line", label: "Line" },
    { id: "bar", label: "Bar" },
    { id: "area", label: "Area" },
];

export const SEASON_MODES = [
    { id: "combined", label: "Combined" },
    { id: "regular", label: "Regular season" },
    { id: "playoffs", label: "Playoffs" },
    { id: "split", label: "Split reg / PO" },
];

export const VALUE_MODES = [
    { id: "totals", label: "Totals" },
    { id: "per_game", label: "Per game" },
];

export const SKATER_METRICS = [
    { id: "points", label: "Points", color: "#7ea862", perGame: true },
    { id: "goals", label: "Goals", color: "#ef767a", perGame: true },
    { id: "assists", label: "Assists", color: "#49beaa", perGame: true },
    { id: "games_played", label: "GP", color: "#6b9bd1", perGame: false },
    { id: "plus_minus", label: "+/-", color: "#ffc857", perGame: true },
    { id: "penalty_minutes", label: "PIM", color: "#c792ea", perGame: true },
    { id: "shots", label: "Shots", color: "#f78c6b", perGame: true },
];

export const GOALIE_METRICS = [
    { id: "wins", label: "Wins", color: "#7ea862", perGame: false },
    { id: "save_percentage", label: "SV%", color: "#49beaa", perGame: false, rate: true },
    { id: "goals_against_average", label: "GAA", color: "#ef767a", perGame: false, rate: true },
    { id: "shutouts", label: "Shutouts", color: "#ffc857", perGame: false },
    { id: "games_played", label: "GP", color: "#6b9bd1", perGame: false },
    { id: "goals_against", label: "GA", color: "#f78c6b", perGame: true },
];

const DEFAULT_SKATER_METRICS = ["points", "goals"];
const DEFAULT_GOALIE_METRICS = ["save_percentage", "wins"];

export function getMetricsForPlayer(isGoalie) {
    return isGoalie ? GOALIE_METRICS : SKATER_METRICS;
}

export function getDefaultGraphPrefs(isGoalie) {
    return {
        chartType: "line",
        seasonMode: "combined",
        valueMode: "totals",
        selectedMetrics: isGoalie ? [...DEFAULT_GOALIE_METRICS] : [...DEFAULT_SKATER_METRICS],
        selectedLeagues: [],
    };
}

export function loadGraphPrefs(playerId, isGoalie) {
    const defaults = getDefaultGraphPrefs(isGoalie);
    if (!playerId || typeof window === "undefined") {
        return defaults;
    }
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaults;
        const stored = JSON.parse(raw);
        const playerPrefs = stored[playerId];
        if (!playerPrefs) return defaults;
        return normalizeGraphPrefs(playerPrefs, isGoalie);
    } catch {
        return defaults;
    }
}

export function getUniqueLeagues(stats) {
    return [...new Set(stats.map((row) => row.league_name).filter(Boolean))].sort();
}

export function sanitizeLeaguesForStats(selectedLeagues, stats) {
    if (!selectedLeagues?.length || !stats?.length) {
        return [];
    }
    const available = new Set(getUniqueLeagues(stats));
    const valid = selectedLeagues.filter((league) => available.has(league));
    return valid;
}

export function normalizeGraphPrefs(prefs, isGoalie, stats = []) {
    const defaults = getDefaultGraphPrefs(isGoalie);
    const validMetrics = getMetricsForPlayer(isGoalie).map((m) => m.id);
    const filteredMetrics = (prefs?.selectedMetrics || defaults.selectedMetrics).filter((id) =>
        validMetrics.includes(id)
    );
    const selectedLeagues = sanitizeLeaguesForStats(prefs?.selectedLeagues, stats);

    return {
        ...defaults,
        ...prefs,
        selectedMetrics: filteredMetrics.length ? filteredMetrics : defaults.selectedMetrics,
        selectedLeagues,
    };
}

export function resolveSelectedMetrics(prefs, isGoalie) {
    const metrics = getMetricsForPlayer(isGoalie);
    const metricIndex = Object.fromEntries(metrics.map((m) => [m.id, m]));
    const defaults = getDefaultGraphPrefs(isGoalie);
    const selected = (prefs?.selectedMetrics || defaults.selectedMetrics).filter((id) => metricIndex[id]);
    return selected.length ? selected : defaults.selectedMetrics;
}

export function saveGraphPrefs(playerId, prefs) {
    if (!playerId || typeof window === "undefined") return;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const stored = raw ? JSON.parse(raw) : {};
        stored[playerId] = prefs;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // ignore storage failures
    }
}

function parseNumber(value) {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
}

function parseOptionalNumber(value) {
    if (value == null || value === "") return null;
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : null;
}

function isPlayoffRow(row) {
    const value = row?.is_playoff;
    if (value === true || value === 1 || value === "1" || value === "true") {
        return true;
    }
    return false;
}

function seasonLabelFromRow(row) {
    if (row.season) return row.season;
    return String(row.year || "").replace(" Playoffs", "");
}

function metricValue(row, metricId, valueMode, metricDef) {
    if (metricDef?.rate) {
        return parseOptionalNumber(row[metricId]);
    }
    if (metricId === "points") {
        // Missing NHLe factor rows intentionally clear G/A/P.
        if (row.nhle_applied === false && row.points == null) {
            return null;
        }
        const points = parseOptionalNumber(row.points);
        if (points != null) return points;
        const goals = parseOptionalNumber(row.goals);
        const assists = parseOptionalNumber(row.assists);
        if (goals == null && assists == null) return null;
        return (goals || 0) + (assists || 0);
    }
    const raw = parseOptionalNumber(row[metricId]);
    if (raw == null) return null;
    if (valueMode === "per_game" && metricDef?.perGame) {
        const gp = parseOptionalNumber(row.games_played);
        if (!gp) return null;
        return raw / gp;
    }
    return raw;
}

function chartMetricValue(value, metricId, metricDef) {
    if (value == null) return null;
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return null;
    if (metricDef?.rate && metricId === "save_percentage" && num > 0 && num <= 1) {
        return num * 100;
    }
    return num;
}

function aggregateRows(rows, metricIds, valueMode, metricIndex) {
    const totals = {};
    const hasValue = {};
    let gpSum = 0;
    let svWeighted = 0;
    let svShots = 0;
    let gaaWeighted = 0;
    let gaaMinutes = 0;

    for (const row of rows) {
        gpSum += parseNumber(row.games_played);
        for (const metricId of metricIds) {
            const def = metricIndex[metricId];
            if (def?.rate && metricId === "save_percentage") {
                const sv = parseOptionalNumber(row.save_percentage);
                const sa = parseOptionalNumber(row.shots_against) || parseOptionalNumber(row.games_played) * 30;
                if (sv != null && sa > 0) {
                    svWeighted += sv * sa;
                    svShots += sa;
                    hasValue[metricId] = true;
                }
            } else if (def?.rate && metricId === "goals_against_average") {
                const gaa = parseOptionalNumber(row.goals_against_average);
                const gp = parseOptionalNumber(row.games_played);
                if (gaa != null && gp > 0) {
                    gaaWeighted += gaa * gp;
                    gaaMinutes += gp;
                    hasValue[metricId] = true;
                }
            } else {
                const value = metricValue(row, metricId, "totals", def);
                if (value == null) continue;
                totals[metricId] = (totals[metricId] || 0) + value;
                hasValue[metricId] = true;
            }
        }
    }

    if (svShots > 0) totals.save_percentage = svWeighted / svShots;
    if (gaaMinutes > 0) totals.goals_against_average = gaaWeighted / gaaMinutes;

    if (valueMode === "per_game" && gpSum > 0) {
        for (const metricId of metricIds) {
            const def = metricIndex[metricId];
            if (def?.rate || !def?.perGame) continue;
            if (!hasValue[metricId]) continue;
            totals[metricId] = (totals[metricId] || 0) / gpSum;
        }
    }

    for (const metricId of metricIds) {
        if (!hasValue[metricId] && !(metricId in totals)) {
            totals[metricId] = null;
        }
    }

    totals.games_played = gpSum;
    totals._gp = gpSum;
    return totals;
}

export function buildChartData(stats, prefs, isGoalie) {
    if (!stats?.length) return [];

    const metrics = getMetricsForPlayer(isGoalie);
    const metricIndex = Object.fromEntries(metrics.map((m) => [m.id, m]));
    const selectedMetrics = resolveSelectedMetrics(prefs, isGoalie);
    if (!selectedMetrics.length) return [];

    let filtered = stats;
    if (prefs.selectedLeagues?.length) {
        filtered = filtered.filter((row) => prefs.selectedLeagues.includes(row.league_name));
    }

    if (prefs.seasonMode === "regular") {
        filtered = filtered.filter((row) => !isPlayoffRow(row));
    } else if (prefs.seasonMode === "playoffs") {
        filtered = filtered.filter((row) => isPlayoffRow(row));
    }

    const groups = new Map();
    for (const row of filtered) {
        const seasonLabel = seasonLabelFromRow(row);
        let key;
        let label;
        let sortKey;

        if (prefs.seasonMode === "split") {
            const seasonNum = parseNumber(row.nhl_season);
            const playoff = isPlayoffRow(row);
            key = `${seasonNum}-${playoff ? "po" : "reg"}`;
            label = playoff ? `${seasonLabel} PO` : seasonLabel;
            sortKey = seasonNum * 10 + (playoff ? 1 : 0);
        } else {
            const seasonNum = parseNumber(row.nhl_season);
            key = String(seasonNum || seasonLabel);
            label = seasonLabel;
            sortKey = seasonNum || 0;
        }

        if (!groups.has(key)) {
            groups.set(key, { key, label, sortKey, rows: [] });
        }
        groups.get(key).rows.push(row);
    }

    return [...groups.values()]
        .sort((a, b) => a.sortKey - b.sortKey)
        .map(({ label, sortKey, rows }) => {
            const aggregated = aggregateRows(rows, selectedMetrics, prefs.valueMode, metricIndex);
            const point = { label, sortKey, games_played: aggregated._gp || 0 };
            for (const metricId of selectedMetrics) {
                point[metricId] = chartMetricValue(
                    aggregated[metricId],
                    metricId,
                    metricIndex[metricId]
                );
            }
            return point;
        });
}

export function countFilteredStats(stats, prefs) {
    if (!stats?.length) {
        return 0;
    }

    let filtered = stats;
    if (prefs.selectedLeagues?.length) {
        filtered = filtered.filter((row) => prefs.selectedLeagues.includes(row.league_name));
    }
    if (prefs.seasonMode === "regular") {
        filtered = filtered.filter((row) => !isPlayoffRow(row));
    } else if (prefs.seasonMode === "playoffs") {
        filtered = filtered.filter((row) => isPlayoffRow(row));
    }
    return filtered.length;
}

export function getEmptyChartMessage(prefs, stats) {
    if (!stats?.length) {
        return "No NHL career stats synced for this player yet.";
    }

    const criteria = [];
    const seasonLabel = SEASON_MODES.find((mode) => mode.id === prefs.seasonMode)?.label;
    if (seasonLabel) {
        criteria.push(`season type "${seasonLabel}"`);
    }
    if (prefs.selectedLeagues?.length) {
        criteria.push(`league${prefs.selectedLeagues.length > 1 ? "s" : ""} ${prefs.selectedLeagues.join(", ")}`);
    }

    if (countFilteredStats(stats, prefs) === 0) {
        if (criteria.length) {
            return `No stats match the current filters (${criteria.join("; ")}). Try adjusting the season or league selection.`;
        }
        return "No stats match the current filters. Try adjusting the season or league selection.";
    }

    return "No chart data available for the selected metrics.";
}

export function getMetricAxis(metricId, metricIndex) {
    const def = metricIndex[metricId];
    return def?.rate ? "rate" : "volume";
}

export function formatMetricValue(metricId, value, metricIndex) {
    if (value == null || Number.isNaN(value)) return "—";
    const def = metricIndex[metricId];
    if (metricId === "save_percentage") {
        const num = parseFloat(value);
        if (!Number.isFinite(num)) return "—";
        if (num > 1) {
            return `${num.toFixed(1)}%`;
        }
        return num >= 1 ? num.toFixed(3) : `.${String(Math.round(num * 1000)).padStart(3, "0")}`;
    }
    if (metricId === "goals_against_average") {
        return parseFloat(value).toFixed(2);
    }
    if (def?.rate || metricId === "plus_minus") {
        return Number(value).toFixed(2);
    }
    if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.001) {
        return Math.round(value);
    }
    return parseFloat(value).toFixed(2);
}
